// App.tsx — top-level router and auth gate for docdaemon. /api is gated on an
// authz.lab0.us session, so the app probes /api/me on load: while that resolves
// it shows a spinner, an authenticated session gets the shell, and an
// unauthenticated one gets lib/web's OidcLoginPage — which renders the embedded
// password form when the backend's /api/oidc/config says this client may take
// credentials, and bounces through the server-side OIDC flow when it may not.
// Locally the server runs ungated and /api/me reports a local user, so the app
// renders immediately.
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import Spinner from 'react-bootstrap/Spinner';
import { LuScrollText } from 'react-icons/lu';
import { OidcLoginPage, Shell, useAuth } from 'common';
import type { ShellNavItem } from 'common';
import { COST_SCOPE, apiClient, hasScope } from './api';
import StatusPage from './pages/StatusPage';
import ReposPage from './pages/ReposPage';
import RepoDetailPage from './pages/RepoDetailPage';
import DocsPage from './pages/DocsPage';
import CostPage from './pages/CostPage';

// Status leads: this is a daemon, and the first question anyone opens it with
// is whether it is keeping up. Shell marks a nav item active on an exact
// pathname match, so every item points at its own canonical path. Docs are not
// a nav item — documentation belongs to a repository, and the way in is that
// repository's card.
const NAV_ITEMS: ShellNavItem[] = [
  { label: 'Status', href: '/' },
  { label: 'Repos', href: '/repos' },
];

function Root() {
  const auth = useAuth({ client: apiClient });

  if (auth.loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!auth.authenticated) {
    // .auth-wrapper is the shared auth-flow surface (DESIGN.md C-AUTH-1): it
    // centers the sign-in card on a tall blank surface instead of pinning it
    // to the top-left corner.
    return (
      <div className="auth-wrapper">
        <OidcLoginPage returnTo={window.location.pathname} />
      </div>
    );
  }

  const logout = () => { window.location.href = '/api/oidc/logout'; };

  // The ledger is its own scope (docdaemon.cost.read) because it is the one
  // page that says what the estate spends. The route stays mounted for
  // everyone — a shared link should say who may read it, not 404 — and the
  // server refuses the fetch behind it, which the page surfaces.
  const nav = hasScope(auth, COST_SCOPE)
    ? [...NAV_ITEMS, { label: 'Cost', href: '/cost' }]
    : NAV_ITEMS;

  return (
    <Shell
      // Icon-only brand: the scroll is the whole mark. `title` gives it the
      // tooltip an unlabelled glyph needs, and the visually-hidden span keeps
      // the Navbar.Brand link an accessible name — an <svg><title> alone is
      // not reliably one.
      brand={<><LuScrollText size={22} title="docdaemon" /><span className="visually-hidden">docdaemon</span></>}
      navItems={nav}
      userEmail={auth.email}
      userPhotoUrl={auth.photoUrl || undefined}
      onLogout={logout}
    >
      <Routes>
        <Route path="/" element={<StatusPage />} />
        {/* auth rides in as a prop rather than a second useAuth() call: the
            pages only need it to decide whether the write controls are on
            offer, and /api/me costs a round-trip to authz for the profile. */}
        <Route path="/repos" element={<ReposPage auth={auth} />} />
        <Route path="/repos/:owner/:name" element={<RepoDetailPage auth={auth} />} />
        <Route path="/docs/:owner/:name" element={<DocsPage />} />
        <Route path="/cost" element={<CostPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Root />
    </BrowserRouter>
  );
}
